import React, { useState } from "react";
import { Flame, Plus, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useMT } from "../lib/miniI18n";
import { useCart } from "../context/CartContext";
import { shareLuckyCard } from "../lib/luckyCard";

export function shareLuckyResult(game, slot, number, nextSlot, date) {
  const fixed = game?.mode !== "interval";
  return shareLuckyCard({ gameName: game?.name || "Lucky", gameId: game?.id, slot, number, nextSlot, date: date || new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }), fixed, category: game?.category });
}

/* Last 10 Lucky results strip with the most frequent digits highlighted; tap a hot digit to quick-bet it as A/B/C single. */
export default function LuckyHotNumbers({ results, game, canBet }) {
  const mt = useMT();
  const { addItem } = useCart();
  const [pick, setPick] = useState(null);
  const singles = (game?.slats || []).filter((s) => ["A", "B", "C"].includes(s.board_type));
  const quick = (digit, slats) => {
    slats.forEach((slat) => addItem({ game_id: game.id, game_name: game.name, slat_id: slat.id, slat_name: slat.name, number: digit, quantity: 1, rate: slat.rate, rate_type: "customer", mode: "direct", perms: 1 }));
    toast.success(`${digit} → ${slats.map((s) => s.board_type).join("/")} ${mt("added to cart — tap Place Bet")}`);
    setPick(null);
  };
  const last = (results || []).slice(0, 10);
  if (!last.length) return null;
  const freq = {};
  last.forEach((r) => String(r.winning_number).split("").forEach((d) => { freq[d] = (freq[d] || 0) + 1; }));
  const ranked = Object.entries(freq).sort((a, b) => b[1] - a[1]);
  const hot = new Set(ranked.slice(0, 3).filter(([, c]) => c >= 2).map(([d]) => d));
  return (
    <div className="rl-card p-3" data-testid="lucky-hot-numbers">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-bold text-slate-700 flex items-center gap-1"><Flame className="w-3.5 h-3.5 text-orange-500" />{mt("Last 10 rounds")}
          <button onClick={() => shareLuckyResult(game, last[0].draw_time, last[0].winning_number, game?.runtime?.draw_slot, last[0].draw_date)} className="ml-1 text-[#25D366] flex items-center gap-0.5 text-[10px] font-bold" data-testid="lucky-share-latest"><MessageCircle className="w-3.5 h-3.5" />{mt("Share")}</button>
        </div>
        <div className="flex items-center gap-1 text-[10px] text-slate-500">{mt("Hot digits")}: {ranked.slice(0, 3).map(([d, c]) => <button key={d} onClick={() => canBet && singles.length && setPick(pick === d ? null : d)} className={`font-black px-1.5 rounded transition-colors ${pick === d ? "bg-orange-500 text-white" : "bg-orange-100 text-orange-700"}`} data-testid={`hot-digit-${d}`}>{d}<span className="font-normal text-[9px]">×{c}</span></button>)}</div>
      </div>
      {pick !== null && (
        <div className="flex items-center gap-1.5 mb-2 rl-fade-up" data-testid="hot-quick-bet">
          <span className="text-[11px] text-slate-600 mr-1">{mt("Quick bet")} <b className="text-orange-600">{pick}</b> {mt("as")}</span>
          {singles.map((s) => <button key={s.id} onClick={() => quick(pick, [s])} className="px-2.5 py-1 rounded-full bg-blue-600 text-white text-[11px] font-black" data-testid={`hot-quick-${s.board_type}`}>{s.board_type} · ₹{s.rate}</button>)}
          <button onClick={() => quick(pick, singles)} className="px-2.5 py-1 rounded-full rl-gold-bg text-slate-900 text-[11px] font-black flex items-center gap-0.5" data-testid="hot-quick-all"><Plus className="w-3 h-3" />{mt("All 3")}</button>
        </div>
      )}
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="lucky-results-strip">
        {last.map((r) => (
          <div key={r.id} className="shrink-0 rounded-xl bg-slate-50 border border-slate-200 px-2 py-1.5 text-center min-w-[58px]">
            <div className="text-[9px] text-slate-400 font-mono">{r.draw_time}</div>
            <div className="flex justify-center gap-0.5 mt-0.5">
              {String(r.winning_number).split("").map((d, i) => <span key={i} className={`w-5 h-5 rounded-full text-[11px] font-black flex items-center justify-center ${hot.has(d) ? "bg-orange-500 text-white shadow" : "bg-blue-600 text-white"}`}>{d}</span>)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
